import { showErrorToast } from "src/components/AlertToastify/AlertToastify";
import { trackEndpointAccess, trackEndpointResponseTime } from "src/utils/analyticsCapFirebase";
import { fetchWithCache } from "src/utils/cacheUtils";

interface ErrorResponse {
  detail?: string;
  message?: string;
}

export const getRosters = async (
  authId,
  userDataPlatform,
  leagueIdPlatform,
  teamName,
  pocToken,
  accessAuthToken,
  signal?: AbortSignal,
  yahooToken?: string,
  forceRefresh = false,
  calculatorSettings?: any
): Promise<any> => {
  try {
    const startedAt = Date.now();
    const url = `${process.env.REACT_APP_BACKEND_URL}/fantasy_league/get_rosters_teams_in_a_league`;
    // Cache key based on league and user for better tracking
    const cacheKey = `rosters-${authId}-${leagueIdPlatform}-${userDataPlatform.user_id}`;

    const requestData: any = {
      settings: {
        pocToken: pocToken,
        leagueToken: userDataPlatform.integration_name === "yahoo" ? yahooToken : userDataPlatform.token,
        user_id: userDataPlatform.user_id,
        league_id: leagueIdPlatform,
        integration_name: userDataPlatform.integration_name,
        // year: "2025",
        sport: "NFL",
      },
      force_refresh: forceRefresh,
      // common_data: commonLeagueData,
      // Add calculator_settings if provided (from LeagueSettings component)
      ...(calculatorSettings && { calculator_settings: calculatorSettings }),
    };

    // If the platform is ESPN, add the specific fields it requires
    if (userDataPlatform.integration_name === "espn") {
      requestData.settings.swid = userDataPlatform.swid;
      requestData.settings.espn_s2 = userDataPlatform.s2;
    }

    const options: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessAuthToken}`,
        "X-Skip-Cache": "true", // this is for avoid using cache for nest js
      },
      body: JSON.stringify(requestData),
      signal,
      credentials: "include",
    };

    const { data, fromCache, ok } = await fetchWithCache(url, options, leagueIdPlatform, userDataPlatform.user_id, authId, forceRefresh);

    // --- TRACK EVENT ON REAL EXECUTION (NOT CACHE) ---
    if (!fromCache && ok) {
      await trackEndpointAccess(authId, userDataPlatform.integration_name, leagueIdPlatform, teamName, "rosters");
      void trackEndpointResponseTime(authId, "rosters", (Date.now() - startedAt) / 1000, userDataPlatform.integration_name, leagueIdPlatform);
    }

    // if (fromCache) {
    //   console.log(`[Rosters Cache] HIT for: ${cacheKey}`);
    // } else {
    //   console.log(`[Rosters Cache] MISS for: ${cacheKey}`);
    // }

    if (!data) {
      console.log("No data received from server");
    }

    if (!ok) {
      const errorData = data as ErrorResponse & { statusCode?: number };
      // Detect rate limit via response body (fetchWithCache doesn't expose HTTP status)
      if (errorData?.statusCode === 429 || errorData?.message === "TOO_MANY_REQUESTS") {
        const err = new Error("TOO_MANY_REQUESTS") as any;
        err.isRateLimit = true;
        err.retryAfter = (errorData as any)?.retryAfter ?? 60;
        throw err;
      }
      throw new Error(errorData.detail || errorData.message);
    }

    return data;
  } catch (error) {
    if (error.name === "AbortError") {
      // Handle abort silently
      console.log("Request was aborted");
      return;
    }
    // Rate limit: show specific message without exposing the raw error code
    if ((error as any).isRateLimit) {
      showErrorToast("You're making too many requests. Please slow down and try again in a moment.");
      throw error;
    }
    console.error("Error in getRosters:", error);
    // console.log("error.message GETROSTERS", error.message);
    showErrorToast(error.message);
    throw error;
  }
};
